//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Button, CircularProgress, Grid, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import { EthAddr } from '@shared/SchemaTypes'

import { OtStoreState, useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'

import { LoanCTO, LoanCTOSchema } from '@shared/LoanCTO'
import { PoolOverviewCTO } from '@shared/sharedTypes'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { logOT } from '@shared/loggingShared'
import { loanApproveTx } from '@react/signedTxs/loanApproveTx'
import { fetchLoanCTO } from '@react/fetchers/PoolFetchers'
import ViewField from '@react/components/elements/ViewField'
import { theme } from '@react/components/theme'

export default function AdminApproveLoanForm(props: { poolAddr: EthAddr; loanAddr: EthAddr }): ReactElement {
  const { poolAddr, loanAddr } = props
  const dispatch = useOtDispatch()

  const poolOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolOverviewCTO
  )
  const chainState = useOtChainState()

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const [loanDetailsCTO, setLoanDetailsCTO] = React.useState<LoanCTO | null>()
  const [loading, setLoading] = React.useState(false)

  useEffect(() => {
    setLoading(true)
    async function fetchApprovalLoanDetails(): Promise<void> {
      if (
        !chainState.chainConfiguration.providerReadOnly ||
        !chainState.stageConfiguration.serverApiBaseUrl ||
        !chainState.stageConfiguration.restAPIKey
      )
        return
      try {
        const loan = await fetchLoanCTO(
          chainState.stageConfiguration.serverApiBaseUrl,
          chainState.stageConfiguration.restAPIKey,
          loanAddr
        )
        setLoanDetailsCTO(loan)
      } catch (err) {
        logOT.error(err)
      } finally {
        setLoading(false)
      }
    }
    fetchApprovalLoanDetails().catch((err) => logOT.error(err))
  }, [loanAddr])

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
  }

  const approveLoanAction = (): void => {
    setIsSubmitted(true)
    dispatch(loanApproveTx({ poolAddr, loanAddr }))
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  if (loading || !loanDetailsCTO)
    return (
      <>
        <CircularProgress />
      </>
    )

  return (
    <div data-testid="LOAN_APPROVE_FORM">
      <Grid alignItems={'stretch'} container spacing={4} sx={{ flexGrow: 1, marginBottom: 8 }} py={2}>
        <Grid item xs={6}>
          <ViewField dataObject={loanDetailsCTO} fieldSchema={LoanCTOSchema.describe().fields.state}></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField dataObject={loanDetailsCTO} fieldSchema={LoanCTOSchema.describe().fields.durationDays}></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={loanDetailsCTO}
            fieldSchema={LoanCTOSchema.describe().fields.principal}
            liquidityTokenSymbol={poolOverviewCTO.liquidityTokenSymbol}
          ></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={loanDetailsCTO}
            fieldSchema={LoanCTOSchema.describe().fields.indicativeApr}
          ></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={loanDetailsCTO}
            fieldSchema={LoanCTOSchema.describe().fields.originationFee}
            liquidityTokenSymbol={poolOverviewCTO.liquidityTokenSymbol}
          ></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={loanDetailsCTO}
            fieldSchema={LoanCTOSchema.describe().fields.transferInWindowDurationDays}
          ></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={loanDetailsCTO}
            fieldSchema={LoanCTOSchema.describe().fields.transferOutWindowDurationDays}
          ></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={loanDetailsCTO}
            fieldSchema={LoanCTOSchema.describe().fields.accrualStartDayTimestamp}
          ></ViewField>
        </Grid>
      </Grid>
      <Button
        disabled={isSubmitted}
        sx={sx}
        onClick={() => {
          approveLoanAction()
        }}
        variant={'contained'}
      >
        <Typography variant={'buttonBold'}>Approve Loan</Typography>
      </Button>
    </div>
  )
}
